/**
 * The inspector for a selected bundle: every link from one group to another, grouped by kind.
 * Clicking a link selects it; the two ends can be opened too. Uses the same filters as the
 * canvas, so it lists exactly the links the bundle stands for.
 */
import { For, Show, createMemo, type JSX } from "solid-js";
import type { Link } from "@protocol/Link";
import { useApp } from "../../app/context";
import { byId } from "../../shared/analysis";
import { shownWith } from "../../shared/categories";
import { linkKindShown } from "../../shared/kinds";
import { bundleIn, endName, parseEnd } from "../../state/bundles";

/** Everything about one bundle. */
export function BundlePanel(props: { bundleKey: string }): JSX.Element {
  const { graph, view, kinds } = useApp();
  /** `true` when the canvas shows this link with the current filters. */
  const shown = (l: Link) => {
    const nodes = byId(graph.state.graph);
    const visible = (id: string) => {
      const n = nodes.get(id);
      return !!n && shownWith(n.categories, view.state.hiddenCategories);
    };
    return linkKindShown(view.state.linkKinds, l.kind) && visible(l.from) && visible(l.to);
  };
  const bundle = createMemo(() => bundleIn(graph.state.graph, props.bundleKey, shown));
  const names = createMemo(() => byId(graph.state.graph));
  /** Selects one end (a group or a node). */
  const open = (end: string) => {
    const { kind, id } = parseEnd(end);
    if (kind === "group") view.selectGroup(id);
    else view.selectNode(id);
  };

  return (
    <Show when={bundle()}>
      {(b) => (
        <>
          <div class="ihead">
            <h2 data-bundle-title>
              {endName(graph.state.graph, b().from)} → {endName(graph.state.graph, b().to)}
            </h2>
            <div class="tags">
              <span class="tag">Bundled links</span>
              <span class="tag">{b().links.length} links</span>
            </div>
          </div>
          <div class="btnrow">
            <button type="button" class="btn" id="bundle-from" onClick={() => open(b().from)}>
              Open {endName(graph.state.graph, b().from)}
            </button>
            <button type="button" class="btn" id="bundle-to" onClick={() => open(b().to)}>
              Open {endName(graph.state.graph, b().to)}
            </button>
          </div>
          <For each={kinds.links().filter((k) => b().links.some((l) => l.kind === k.id))}>
            {(k) => {
              const links = () => b().links.filter((l) => l.kind === k.id);
              return (
                <section class="grp">
                  <h3>
                    {k.label} <span>{links().length}</span>
                  </h3>
                  <ul>
                    <For each={links()}>
                      {(l) => (
                        <li>
                          <button type="button" class="ref" data-bundle-link={`${l.from}>${l.to}`} onClick={() => view.selectLink(l)}>
                            <span>
                              {names().get(l.from)?.name ?? l.from} <em>{kinds.ofLink(l).verb}</em> {names().get(l.to)?.name ?? l.to}
                            </span>
                          </button>
                        </li>
                      )}
                    </For>
                  </ul>
                </section>
              );
            }}
          </For>
          <p class="hint2">On the graph, the links between two groups are drawn as one line with a count. Select a group or a node to see its links one by one.</p>
        </>
      )}
    </Show>
  );
}
